import { Button, Typography } from "@mui/material";
import clsx from "clsx";
import _ from "lodash";
import pluralize from "pluralize";
import React, { useState } from "react";
import { useDataMapPendingReceivedPicIds } from "../../../common/service/resources/data-map/dataMapPending";
import {
  useDataMapReceived,
  useDataMapReceivedPIC,
} from "../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataSourceDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../common/service/server/Types";
import { UseReceivedForCollectionGroups } from "../../hooks/useReceivedForCollectionGroups";
import { SOURCE_KEY_CONSUMER_DIRECTLY } from "../../types/Vendor";
import { CalloutVariant } from "../Callout";
import { DataRecipientCollapseCard } from "../DataRecipientCollapseCard";
import { DataSourceReceiptCheckboxes } from "./DataSourceReceiptCheckboxes";

type DataSourceReceiptCardsProps = {
  dataMap: DataMapDto;
  receivedData: UseReceivedForCollectionGroups;
  groups: CollectionGroupDetailsDto[];
  consumerDirectlyName?: string;
  showPendingSources?: boolean;
};

export const DataSourceReceiptCards: React.FC<DataSourceReceiptCardsProps> = ({
  dataMap,
  receivedData,
  groups,
  consumerDirectlyName = "Consumer Directly",
  showPendingSources,
}) => {
  const sources = showPendingSources ? receivedData.sources : receivedData.sourcesWithCollectedPIC;
  const [expanded, setExpanded] = useState<null | string>(sources[0]?.vendorId);
  const handleExpand = (vendorId: UUIDString) => {
    setExpanded(vendorId);
    setTimeout(() => window.polaris.scrollSelectorIntoView(`#dr-${vendorId}`), 300);
  };

  return (
    <>
      {sources.map((r, idx) => {
        const next = idx != sources.length - 1 ? sources[idx + 1] : null;

        return (
          <CardWrapper
            key={r.vendorId}
            r={r}
            dataMap={dataMap}
            receivedData={receivedData}
            groups={groups}
            next={next}
            expanded={expanded == r.vendorId}
            onExpand={handleExpand}
            consumerDirectlyName={consumerDirectlyName}
          />
        );
      })}
    </>
  );
};

type CardWrapperProps = {
  r: OrganizationDataSourceDto;
  dataMap: DataMapDto;
  expanded: boolean;
  next: OrganizationDataSourceDto | null;
  receivedData: UseReceivedForCollectionGroups;
  groups: CollectionGroupDetailsDto[];
  onExpand: (vendorId: UUIDString) => void;
  consumerDirectlyName: string;
};
const CardWrapper: React.FC<CardWrapperProps> = ({
  r,
  dataMap,
  expanded,
  next,
  receivedData,
  groups,
  onExpand,
  consumerDirectlyName,
}) => {
  const sourceReceived = useDataMapReceived(dataMap, {
    vendorId: r.vendorId,
    collectionGroupId: groups.map((g) => g.id),
  });
  const sourceCount = sourceReceived.length;
  const sourceLabel = pluralize("Categories", sourceCount, true);
  const recipientName =
    r.vendorKey == SOURCE_KEY_CONSUMER_DIRECTLY ? consumerDirectlyName : undefined;

  return (
    <div id={`dr-${r.vendorId}`} key={r.vendorId}>
      <DataRecipientCollapseCard
        recipient={r}
        recipientName={recipientName}
        collapsed={!expanded}
        variant={CalloutVariant.LightPurpleBordered}
        onExpand={() => onExpand(r.vendorId)}
        headerContent={
          <Typography
            variant="caption"
            className={clsx({
              "text-error": sourceCount === 0,
              "text-muted": sourceCount > 0,
            })}
          >
            {sourceLabel} Selected
          </Typography>
        }
      >
        <div>
          <CheckboxesWrapper
            dataMap={dataMap}
            receiptData={receivedData}
            source={r}
            groups={groups}
          />
        </div>

        {Boolean(next) && (
          <Button
            className="mt-lg mr-lg"
            variant="contained"
            size="small"
            onClick={() => onExpand(next!.vendorId)}
          >
            Next Source
          </Button>
        )}
      </DataRecipientCollapseCard>
    </div>
  );
};

type CheckboxesWrapperProps = {
  dataMap: DataMapDto;
  receiptData: UseReceivedForCollectionGroups;
  source: OrganizationDataSourceDto;
  groups: CollectionGroupDetailsDto[];
};
const CheckboxesWrapper: React.FC<CheckboxesWrapperProps> = ({
  dataMap,
  receiptData,
  source,
  groups,
}) => {
  const selectedPic = useDataMapReceivedPIC(dataMap, {
    vendorId: source.vendorId,
    collectionGroupId: groups.map((g) => g.id),
  });
  const selectedPicIds = _.uniq(selectedPic.map((p) => p.id));
  const pendingPicIds = useDataMapPendingReceivedPicIds(dataMap, {
    vendorId: source.vendorId,
    collectionGroupId: groups.map((g) => g.id),
  });

  return (
    <DataSourceReceiptCheckboxes
      includeSelectAll={source.vendorKey === "source-consumer-directly"}
      collectedPic={receiptData.collectedPIC}
      selected={selectedPicIds}
      pending={pendingPicIds}
      onSelectAll={(received: boolean) => {
        receiptData.actions.updateReceived({
          vendorId: source.vendorId,
          receivedIds: receiptData.collectedPIC.map((pic) => pic.id),
          received,
        });
      }}
      onChange={(receivedId: UUIDString, received: boolean) => {
        receiptData.actions.updateReceived({
          vendorId: source.vendorId,
          receivedIds: [receivedId],
          received,
        });
      }}
    />
  );
};
